/**
 * The live transcript, styled like a messaging thread: borderless rounded
 * bubbles — muted for the assistant on the left, primary-on-primary for the
 * caller on the right — with a small timestamp under each finished message and
 * no speaker labels at all; the sides and the colours carry the roles.
 *
 * WHERE THESE WORDS COME FROM, because it changes how much they can be trusted:
 * every line here arrived over /control from the Durable Object, which read it
 * off the provider sideband with our own API key. The browser is not the source
 * and cannot edit the record — it is being shown a sanitised projection. That is
 * why there is no local speech recognition in this file and no attempt to
 * reconcile what the microphone heard with what the provider transcribed.
 *
 * TOOL CALLS RENDER AS CARDS in the flow, anchored where the conversation stood
 * when the model reached for the tool: an icon, a status line (running /
 * completed with duration / failed), the tool's name as a title, and — when the
 * page could fetch the tool library — its description. NAME AND STATE ONLY ride
 * the socket, deliberately: public embed visitors hold the same channel, so
 * arguments and results never travel it; the description is looked up by the
 * authed page, not received from the call. Full args/results live on the
 * session page.
 *
 * `show_info` SCREEN CARDS render in the same flow, by the same anchoring —
 * and their body is the one exception to "name and state only", carried whole
 * because it is content the model composed FOR the screen, the same trust
 * class as the words it speaks. The body only ever reaches the DOM through the
 * whitelist renderer in lib/mini-markdown.tsx: React elements, https-only
 * links, never HTML.
 *
 * Timestamps are the line's ARRIVAL time on this browser's clock — display
 * furniture, not measurement. The latency numbers with meaning are on the
 * session page, on the Durable Object's clock.
 *
 * Non-final lines are rendered faint with a pulse and no timestamp. A partial
 * that never gets a final is a real and common state — the sideband can drop
 * mid-turn — and it should look unfinished rather than authoritative.
 *
 * The auto-scroll follows the bottom ONLY while the reader is already at the
 * bottom. Yanking the viewport back down while somebody is reading earlier turns
 * is the single most annoying thing a live transcript can do.
 */
import { useEffect, useRef } from 'react'
import { CheckIcon, SquareFunctionIcon, XIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { renderMiniMarkdown } from '@/lib/mini-markdown'
import type { CardActivity, ToolActivity, TranscriptLine } from '@/lib/call'

const NEAR_BOTTOM_PX = 64

/** Extra context for tool cards, keyed by tool name. Optional: the transcript
 *  renders honestly without it, just with less to say. The display fields are
 *  the operator-written label/blurb off the tool library; the wire's own copy
 *  (on ToolActivity) wins when present, because it is the session's snapshot. */
export interface ToolMeta {
  description: string
  displayName?: string | null
  displayDescription?: string | null
}

function stamp(atMs: number): string {
  return new Date(atMs).toLocaleTimeString()
}

/** `get_weather` → `Get weather`: a title, not a rename — the mono name is
 *  what the record uses, so it stays visible in the card's status line. */
function titleOf(name: string): string {
  const words = name.replace(/[_-]+/g, ' ').trim()
  return words === '' ? name : words[0]!.toUpperCase() + words.slice(1)
}

function ToolCard({ tool, meta }: { tool: ToolActivity; meta: ToolMeta | undefined }) {
  const seconds =
    tool.endedAtMs === null ? null : Math.max(0, (tool.endedAtMs - tool.startedAtMs) / 1000)
  return (
    <div className="flex max-w-[26rem] flex-col gap-0.5 self-start">
      <div className="rounded-2xl bg-muted px-4 py-3">
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <SquareFunctionIcon className="size-3.5" aria-hidden />
          {tool.state === 'running' ? (
            <span className="animate-pulse">Running…</span>
          ) : tool.state === 'ok' ? (
            <span className="flex items-center gap-1">
              <CheckIcon className="size-3" aria-hidden />
              Completed{seconds !== null ? ` · ${seconds.toFixed(1)}s` : ''}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-ev-bad">
              <XIcon className="size-3" aria-hidden />
              Failed{seconds !== null ? ` · ${seconds.toFixed(1)}s` : ''}
            </span>
          )}
        </p>
        <p className="mt-1 text-sm font-semibold">
          {tool.displayName ?? meta?.displayName ?? titleOf(tool.name)}
        </p>
        {(() => {
          // The session's own snapshot first, the library second, honesty last.
          const blurb =
            tool.displayDescription ?? meta?.displayDescription ?? meta?.description ?? null
          return blurb !== null ? (
            <p className="mt-0.5 text-sm text-muted-foreground">{blurb}</p>
          ) : (
            <p className="mono mt-0.5 text-xs text-muted-foreground">{tool.name}</p>
          )
        })()}
      </div>
      <span className="px-1 text-[0.7rem] text-muted-foreground/70">{stamp(tool.startedAtMs)}</span>
    </div>
  )
}

/**
 * The `show_info` screen card, in the transcript flow — the operator's view of
 * the same object the embed's chat panel paints (see InfoCard in
 * components/voice/chat-flag.tsx; a customer watching a call on both should see
 * the same card). Same bubble as ToolCard; the body goes through the whitelist
 * renderer and nothing else. The style argument is empty because this page has
 * Tailwind — the renderer's own defaults carry spacing, indent and markers.
 */
function InfoCard({ card }: { card: CardActivity }) {
  return (
    <div className="flex max-w-[26rem] flex-col gap-0.5 self-start">
      <div className="rounded-2xl bg-muted px-4 py-3">
        {card.title !== null ? <p className="mb-1 text-sm font-semibold">{card.title}</p> : null}
        <div className="text-sm">{renderMiniMarkdown(card.body, {})}</div>
      </div>
      <span className="px-1 text-[0.7rem] text-muted-foreground/70">{stamp(card.atMs)}</span>
    </div>
  )
}

export function LiveTranscript({
  lines,
  tools = [],
  cards = [],
  toolMeta = {},
  speaking,
  className,
}: {
  lines: readonly TranscriptLine[]
  /** Tool activity to interleave, anchored by `anchorIndex` line position. */
  tools?: readonly ToolActivity[]
  /** `show_info` screen cards, interleaved by the same anchoring. */
  cards?: readonly CardActivity[]
  /** Library context per tool name, when the page has it. See ToolMeta. */
  toolMeta?: Record<string, ToolMeta>
  speaking: { user: boolean; assistant: boolean }
  className?: string
}) {
  const boxRef = useRef<HTMLDivElement | null>(null)
  const stick = useRef(true)

  useEffect(() => {
    const box = boxRef.current
    if (!box || !stick.current) return
    box.scrollTop = box.scrollHeight
  }, [lines, tools, cards])

  /** Tools anchored after line i-1 (i.e. when i lines existed). Clamped so a
   *  chip can never be orphaned by the line-cap trimming old transcript. */
  const toolsAt = (index: number) =>
    tools.filter((t) => Math.min(t.anchorIndex, lines.length) === index)

  /** Cards, same rule, same clamp. At a shared index the tool chip renders
   *  first: the `show_info` chip's `running` frame precedes its card frame. */
  const cardsAt = (index: number) =>
    cards.filter((c) => Math.min(c.anchorIndex, lines.length) === index)

  return (
    <div
      ref={boxRef}
      onScroll={() => {
        const box = boxRef.current
        if (!box) return
        stick.current = box.scrollHeight - box.scrollTop - box.clientHeight < NEAR_BOTTOM_PX
      }}
      className={cn('flex flex-col gap-3 overflow-y-auto px-4 py-4', className)}
      aria-live="polite"
      aria-atomic="false"
    >
      {lines.length === 0 && tools.length === 0 && cards.length === 0 ? (
        <p className="empty text-sm">
          Nothing said yet. The transcript arrives from the session’s Durable Object, not from this browser.
        </p>
      ) : null}

      {toolsAt(0).map((t) => (
        <ToolCard key={t.callId} tool={t} meta={toolMeta[t.name]} />
      ))}
      {cardsAt(0).map((c) => (
        <InfoCard key={c.callId} card={c} />
      ))}

      {lines.map((line, i) => {
        const isUser = line.who === 'user'
        return (
          <div key={line.itemId} className="contents">
            <div className={cn('flex flex-col gap-0.5', isUser ? 'items-end text-right' : 'items-start')}>
              <p
                className={cn(
                  'max-w-[34rem] rounded-2xl px-4 py-2.5 text-[0.9375rem] leading-relaxed',
                  isUser
                    ? 'rounded-br-md bg-primary text-primary-foreground'
                    : 'rounded-bl-md bg-muted text-foreground',
                  !line.final && 'opacity-70',
                )}
              >
                {line.text}
                {!line.final ? <span className="ml-1 animate-pulse">…</span> : null}
              </p>
              {line.final && (
                <span className="px-1 text-[0.7rem] text-muted-foreground/70">{stamp(line.atMs)}</span>
              )}
            </div>
            {toolsAt(i + 1).map((t) => (
              <ToolCard key={t.callId} tool={t} meta={toolMeta[t.name]} />
            ))}
            {cardsAt(i + 1).map((c) => (
              <InfoCard key={c.callId} card={c} />
            ))}
          </div>
        )
      })}

      {speaking.assistant ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="live-dot" aria-hidden />
          the assistant is speaking
        </div>
      ) : speaking.user ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="live-dot" aria-hidden />
          listening
        </div>
      ) : null}
    </div>
  )
}
